const BALANCE_METRIC = "Ativo Total"
const INDICATOR_METRIC = "ROA"

const hasOwn = (record, field) => Object.prototype.hasOwnProperty.call(record, field)

const normalizeFinancialUnit = (unit) => (
    typeof unit === "string" && unit.trim() ? unit.trim() : "R$"
)

const isValidFinancialUnit = (unit) => unit == null || typeof unit === "string"

const isCanonicalRecord = (record) => Boolean(
    record
    && typeof record === "object"
    && record.DataReferencia
    && typeof record.DadoFinanceiro === "string"
    && hasOwn(record, "ValorDadoFinanceiro")
)

const isLegacyRecord = (record) => Boolean(
    record
    && typeof record === "object"
    && record.DataReferencia
    && (hasOwn(record, "AtivoTotal") || hasOwn(record, "ROA"))
)

const normalizeLegacyRecords = (records) => {
    if (!records.every(isLegacyRecord)) return null

    return records.flatMap((record) => [
        hasOwn(record, "AtivoTotal")
            ? { DataReferencia: record.DataReferencia, DadoFinanceiro: BALANCE_METRIC, ValorDadoFinanceiro: record.AtivoTotal }
            : null,
        hasOwn(record, "ROA")
            ? { DataReferencia: record.DataReferencia, DadoFinanceiro: INDICATOR_METRIC, ValorDadoFinanceiro: record.ROA }
            : null,
    ].filter(Boolean))
}

export const isRecognizedBalancePayload = (payload) => {
    if (!Array.isArray(payload)) return false

    if (Array.isArray(payload[0])) {
        return payload.length === 2
            && payload[0].every(isCanonicalRecord)
            && isValidFinancialUnit(payload[1])
    }

    return payload.every(isLegacyRecord)
}

export const normalizeBalancePayload = (payload) => {
    if (!Array.isArray(payload)) return { records: [], unit: "R$" }

    if (Array.isArray(payload[0])) {
        if (payload.length !== 2) return { records: [], unit: "R$" }
        const [records, unit] = payload
        return {
            records: Array.isArray(records) && records.every(isCanonicalRecord) ? records : [],
            unit: normalizeFinancialUnit(unit),
        }
    }

    const records = normalizeLegacyRecords(payload)
    const legacyUnit = payload.find((record) => record?.OrdemGrandezaAtivo)?.OrdemGrandezaAtivo

    return {
        records: records ?? [],
        unit: normalizeFinancialUnit(legacyUnit),
    }
}

export const formatFinancialPeriod = (period) => {
    const [year, month] = String(period ?? "").split("-")

    return year && month ? `${month}/${year}` : "-"
}

export const getLatestFinancialPeriod = (records) => {
    if (!Array.isArray(records) || records.length === 0) return "-"

    const periods = [...new Set(
        records.map((record) => record.DataReferencia).filter(Boolean)
    )].sort((a, b) => b.localeCompare(a))

    return formatFinancialPeriod(periods[0])
}

const buildMetricRow = (records, periods, metric) => ({
    id: metric,
    label: metric,
    values: periods.map(
        (period) => records.find(
            (record) => record.DataReferencia === period && record.DadoFinanceiro === metric
        )?.ValorDadoFinanceiro ?? null
    ),
})

const hasValue = (value) => value !== null && value !== undefined && value !== ""

export const buildFinancialSeries = (records) => {
    const periods = [...new Set(
        records.map((record) => record.DataReferencia).filter(Boolean)
    )].sort((a, b) => b.localeCompare(a))

    const hasBalance = records.some((record) => record.DadoFinanceiro === BALANCE_METRIC && hasValue(record.ValorDadoFinanceiro))
    const hasIndicator = records.some((record) => record.DadoFinanceiro === INDICATOR_METRIC && hasValue(record.ValorDadoFinanceiro))

    return {
        periods,
        balanceRows: hasBalance
            ? [buildMetricRow(records, periods, BALANCE_METRIC)]
            : [],
        indicatorRows: hasIndicator
            ? [buildMetricRow(records, periods, INDICATOR_METRIC)]
            : [],
    }
}
